import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Identity,
  Inline,
  LinkButton,
  Pagination,
  SearchInput,
  SegmentedControl,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  Toolbar,
  ToolbarCount,
  ToolbarItem,
  ToolbarSpacer,
  VisuallyHidden,
} from '@hkdevs/design-system';
import { useEnabledRoles } from '../../api/queries/roles';
import { usePeople, type PeopleRole } from '../../api/queries/people';
import { RouterLink } from '../../shell/RouterLink';
import { ROLE_LABELS } from '../users/UserModal';
import { ROLE_TONES } from '../users/UsersView';

export const PAGE_SIZE = 25;

/** The People page's URL state (ADR 0023), as on Users: the search, the role and the page. */
export interface PeopleSearch {
  q?: string | undefined;
  role?: PeopleRole | undefined;
  /** 1-based; left out for the first. */
  page?: number | undefined;
}

const ROLES: PeopleRole[] = ['supervisor', 'rep', 'rep_intern'];

export function parsePeopleSearch(search: Record<string, unknown>): PeopleSearch {
  const page = Number(search.page);
  return {
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    role: ROLES.find((role) => role === search.role),
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

/** The key for the signed-in user's People search, or for a viewed pay viewer's (A20). */
const lastSearch = (viewAs: string | null) =>
  viewAs ? `people.search.${viewAs}` : 'people.search';

/**
 * Remembers People's search (its URL's query string) for this tab and identity, so the
 * person pages' breadcrumb goes back to the same list. Session storage may be
 * unavailable: then People opens unfiltered.
 */
export function rememberPeopleSearch(searchStr: string, viewAs: string | null = null) {
  try {
    sessionStorage.setItem(lastSearch(viewAs), searchStr);
  } catch {
    // Storage blocked: nothing to remember.
  }
}

/** People, with the search last used in this tab by this identity (no /view-as/ prefix). */
export function peopleHref(viewAs: string | null = null): string {
  try {
    const searchStr = sessionStorage.getItem(lastSearch(viewAs)) ?? '';
    return searchStr.startsWith('?') ? `/people${searchStr}` : '/people';
  } catch {
    return '/people';
  }
}

const plural = (count: number) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? 'person' : 'people'}`;

export interface PeopleViewProps {
  search: PeopleSearch;
  onSearchChange: (next: PeopleSearch) => void;
}

/**
 * A pay viewer's People page (ADR 0028): the users whose pay they may view, with View pay
 * (a link to their pay under /people/$userId, A19) and nothing else. The Users list,
 * slimmed: no create, import, export, edit or details.
 */
export function PeopleView({ search, onSearchChange }: PeopleViewProps) {
  const { data: enabledRoles } = useEnabledRoles();
  // Rep (intern) is offered only while the tenant has the role switched on.
  const internsOn = !!enabledRoles?.includes('rep_intern');
  const filters: { value: 'all' | PeopleRole; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'rep', label: 'Rep' },
    ...(internsOn ? [{ value: 'rep_intern' as const, label: 'Rep (intern)' }] : []),
  ];
  const role = search.role === 'rep_intern' && !internsOn ? undefined : search.role;
  const page = search.page ?? 1;
  const q = search.q?.trim() || undefined;
  const people = usePeople({ q, role, page, pageSize: PAGE_SIZE });

  /** New filters go back to the first page. */
  const set = (patch: Partial<PeopleSearch>) =>
    onSearchChange({ ...search, ...patch, page: undefined });

  let body;
  if (people.isError) {
    body = (
      <Alert
        tone="danger"
        title="People couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void people.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (people.data && people.data.total === 0) {
    body = (
      <Card>
        <CardBody>
          {q || role ? (
            <EmptyState
              title="No matching people"
              description="No one matches your search or filter. Try a different name, email, or role."
              icon="search"
              headingLevel={2}
              actions={
                <Button size="sm" variant="secondary" onClick={() => onSearchChange({})}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No one to show yet"
              description="Reps and supervisors appear here once an admin adds them."
              icon="users"
              headingLevel={2}
            />
          )}
        </CardBody>
      </Card>
    );
  } else {
    const data = people.data;
    const pageCount = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
    body = (
      <>
        <Card flush>
          <Table aria-label="People" responsive="cards" busy={!data || people.isFetching}>
            <TableHead>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>
                <VisuallyHidden>Actions</VisuallyHidden>
              </TableCell>
            </TableHead>
            <TableBody>
              {!data ? (
                <TableLoading colSpan={4} rows={6} label="Loading people" />
              ) : (
                data.items.map((person) => (
                  <TableRow key={person.id}>
                    <TableCell rowHeader>
                      <Identity
                        name={person.name}
                        size="sm"
                        {...(person.pictureUrl && { avatarSrc: person.pictureUrl })}
                        {...(!person.enabled && {
                          secondary: (
                            <Badge tone="danger" size="sm">
                              Disabled
                            </Badge>
                          ),
                        })}
                      />
                    </TableCell>
                    <TableCell label="Email" tone="muted">
                      {person.email}
                    </TableCell>
                    <TableCell label="Roles">
                      <Inline as="span" gap={1} align="stretch">
                        {person.roles.map((held) => (
                          <Badge key={held} tone={ROLE_TONES[held]} size="sm">
                            {ROLE_LABELS[held]}
                          </Badge>
                        ))}
                      </Inline>
                    </TableCell>
                    <TableCell>
                      <Inline as="span" gap={2} justify="end" wrap={false}>
                        {/* A link: the browser opens it here or in a new tab (A19). */}
                        <LinkButton
                          as={RouterLink}
                          href={`/people/${person.id}`}
                          aria-label={`View ${person.name}’s pay`}
                        >
                          View pay
                        </LinkButton>
                      </Inline>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
        {data && pageCount > 1 && (
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(next) =>
              onSearchChange({ ...search, page: next > 1 ? next : undefined })
            }
            size="sm"
          />
        )}
      </>
    );
  }

  return (
    <Stack gap={3} shrink>
      <Toolbar aria-label="Filter people">
        <ToolbarItem size="search">
          <SearchInput
            label="Search people by name or email"
            placeholder="Search by name or email"
            value={search.q ?? ''}
            onValueChange={(next) => set({ q: next || undefined })}
          />
        </ToolbarItem>
        <SegmentedControl
          aria-label="Role"
          size="sm"
          options={filters}
          value={role ?? 'all'}
          onChange={(next) => set({ role: next === 'all' ? undefined : (next as PeopleRole) })}
        />
        <ToolbarSpacer />
        {people.data && <ToolbarCount>{plural(people.data.total)}</ToolbarCount>}
      </Toolbar>
      {body}
    </Stack>
  );
}
